import React, { useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col,
  Spinner
} from 'reactstrap';
import axios from 'axios';

const CreateEvidence = ({ sspId, onBack, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'other',
    url: '',
    notes: '',
    linkedControls: '',
    linkedSections: '',
    sensitivity: 'internal',
    cadenceDays: 30,
    ssp_id: sspId || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const parseArrayInput = (value) => {
    if (!value) return [];
    return value.split(',').map(v => v.trim()).filter(v => v);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Evidence name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        ...formData,
        linkedControls: parseArrayInput(formData.linkedControls),
        linkedSections: parseArrayInput(formData.linkedSections)
      };

      const response = await axios.post(
        `${baseURL}/ssp-studio/evidence`,
        payload,
        { withCredentials: true }
      );

      if (response.data.success) {
        if (onCreated) onCreated(response.data.data);
      } else {
        setError(response.data.message || 'Failed to create evidence');
      }
    } catch (err) {
      console.error('Error creating evidence:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <Button color="secondary" outline size="sm" onClick={onBack}>
            <i className="mdi mdi-arrow-left me-1"></i>
            Back to Library
          </Button>
        </Col>
      </Row>

      <Card>
        <CardHeader>
          <i className="mdi mdi-plus-circle me-2"></i>
          Add New Evidence
        </CardHeader>
        <CardBody>
          {error && (
            <Alert color="danger" toggle={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={8}>
                <FormGroup>
                  <Label for="name">
                    Evidence Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g., SSL/TLS Configuration Scan Report"
                    disabled={saving}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="type">Type</Label>
                  <Input
                    type="select"
                    id="type"
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                    disabled={saving}
                  >
                    <option value="scan">Scan Report</option>
                    <option value="policy">Policy Document</option>
                    <option value="procedure">Procedure Document</option>
                    <option value="screenshot">Screenshot</option>
                    <option value="config">Configuration File</option>
                    <option value="ticket">Support Ticket</option>
                    <option value="other">Other</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="url">URL / File Path</Label>
              <Input
                type="text"
                id="url"
                value={formData.url}
                onChange={(e) => handleChange('url', e.target.value)}
                placeholder="https://example.com/evidence/scan-report.pdf"
                disabled={saving}
              />
              <small className="text-muted">
                Link to evidence file (SharePoint, Google Drive, S3, etc.)
              </small>
            </FormGroup>

            <FormGroup>
              <Label for="notes">Notes / Description</Label>
              <Input
                type="textarea"
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={4}
                placeholder="Describe this evidence artifact and how it demonstrates control effectiveness..."
                disabled={saving}
              />
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="linkedControls">Linked NIST Controls</Label>
                  <Input
                    type="text"
                    id="linkedControls"
                    value={formData.linkedControls}
                    onChange={(e) => handleChange('linkedControls', e.target.value)}
                    placeholder="e.g., AC-1, SC-8, SI-2"
                    disabled={saving}
                  />
                  <small className="text-muted">
                    Comma-separated list of control IDs
                  </small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="linkedSections">Linked SSP Sections</Label>
                  <Input
                    type="text"
                    id="linkedSections"
                    value={formData.linkedSections}
                    onChange={(e) => handleChange('linkedSections', e.target.value)}
                    placeholder="e.g., sec-3, sec-4"
                    disabled={saving}
                  />
                  <small className="text-muted">
                    Comma-separated list of section IDs
                  </small>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="sensitivity">Sensitivity Level</Label>
                  <Input
                    type="select"
                    id="sensitivity"
                    value={formData.sensitivity}
                    onChange={(e) => handleChange('sensitivity', e.target.value)}
                    disabled={saving}
                  >
                    <option value="public">Public</option>
                    <option value="internal">Internal Use Only</option>
                    <option value="cui">Controlled Unclassified Information (CUI)</option>
                    <option value="sensitive">Sensitive/Confidential</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="cadenceDays">
                    Refresh Cadence (days)
                  </Label>
                  <Input
                    type="number"
                    id="cadenceDays"
                    value={formData.cadenceDays}
                    onChange={(e) => handleChange('cadenceDays', parseInt(e.target.value))}
                    min={1}
                    max={365}
                    disabled={saving}
                  />
                  <small className="text-muted">
                    How often this evidence should be updated
                  </small>
                </FormGroup>
              </Col>
            </Row>

            <hr className="my-4" />

            <div className="d-flex justify-content-end gap-2">
              <Button color="secondary" outline onClick={onBack} disabled={saving}>
                Cancel
              </Button>
              <Button color="success" type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-check me-1"></i>
                    Create Evidence
                  </>
                )}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default CreateEvidence;
